import { Graphics, RenderTexture, Texture, Sprite, Application } from 'pixi.js-legacy';
import type { BossType } from '../../types';

/**
 * 보스 텍스쳐 캐시
 * 보스 타입별 + 체력 레벨별 RenderTexture를 캐싱하여 재사용
 * 체력은 5% 단위로 21단계 (0%, 5%, 10%, ..., 100%)
 */
export class BossTextureCache {
    private static instance: BossTextureCache | null = null;
    // 캐시 키: "{bossType}_{healthLevel}_{radius}" (예: "rotatingTurret_20_30")
    private textures: Map<string, Texture> = new Map();
    private app: Application | null = null;
    private initialized: boolean = false;

    // 기본 보스 반경
    private readonly DEFAULT_RADIUS = 30;
    // 체력 레벨 단계 수 (0~20, 5% 단위)
    private readonly HEALTH_LEVELS = 21;
    // 텍스쳐 패딩 (포탑 팔 등 외곽 요소를 위한 여유)
    private readonly TEXTURE_PADDING = 1.3;

    private constructor() {}

    static getInstance(): BossTextureCache {
        if (!BossTextureCache.instance) {
            BossTextureCache.instance = new BossTextureCache();
        }
        return BossTextureCache.instance;
    }

    /**
     * 초기화 및 기본 텍스쳐 생성
     */
    initialize(app: Application): void {
        if (this.initialized) return;

        this.app = app;

        // 기본 반경으로 모든 보스 타입의 텍스쳐 미리 생성
        const bossTypes: BossType[] = ['rotatingTurret', 'chaser', 'splitter'];

        for (const type of bossTypes) {
            this.preGenerateTextures(type, this.DEFAULT_RADIUS);
        }

        this.initialized = true;
    }

    /**
     * 특정 보스 타입의 모든 체력 레벨 텍스쳐 미리 생성
     */
    private preGenerateTextures(type: BossType, radius: number): void {
        for (let level = 0; level < this.HEALTH_LEVELS; level++) {
            this.getOrCreateTexture(type, level, radius);
        }
    }

    /**
     * 보스 텍스쳐 가져오기 (없으면 생성)
     */
    getOrCreateTexture(type: BossType, healthLevel: number, radius: number): Texture | null {
        if (!this.app) return null;

        const key = `${type}_${healthLevel}_${radius}`;

        if (this.textures.has(key)) {
            return this.textures.get(key)!;
        }

        // 새 텍스쳐 생성
        const texture = this.createBossTexture(type, healthLevel, radius);
        if (texture) {
            this.textures.set(key, texture);
        }

        return texture;
    }

    /**
     * 체력 비율을 레벨로 변환 (5% 단위)
     */
    healthRatioToLevel(healthRatio: number): number {
        return Math.round(healthRatio * 20);
    }

    /**
     * 보스 텍스쳐 생성
     */
    private createBossTexture(type: BossType, healthLevel: number, radius: number): Texture | null {
        if (!this.app) return null;

        const size = radius * 2 * this.TEXTURE_PADDING;
        const healthRatio = healthLevel / 20;

        const g = new Graphics();

        // 중심을 텍스쳐 중앙으로 이동
        const offset = size / 2;

        switch (type) {
            case 'rotatingTurret':
                this.drawRotatingTurret(g, offset, offset, radius, healthRatio);
                break;
            case 'chaser':
                this.drawChaser(g, offset, offset, radius, healthRatio);
                break;
            case 'splitter':
                this.drawSplitter(g, offset, offset, radius, healthRatio, 0);
                break;
        }

        // RenderTexture 생성
        const renderTexture = RenderTexture.create({
            width: size,
            height: size,
            resolution: 2 // 고해상도 지원
        });

        // Graphics를 텍스쳐로 렌더링 (v7 API)
        this.app.renderer.render(g, { renderTexture });

        g.destroy();

        return renderTexture;
    }

    /**
     * RotatingTurret 그리기 (포탑 팔 제외 - 회전하므로 별도 처리)
     */
    private drawRotatingTurret(g: Graphics, cx: number, cy: number, radius: number, healthRatio: number): void {
        // 외곽 원
        g.lineStyle(2, 0xFFFFFF);
        g.drawCircle(cx, cy, radius);

        // 체력 표시 원
        g.lineStyle(1, 0xFFFFFF, 0.5);
        g.drawCircle(cx, cy, radius * healthRatio);

        // 포탑 팔 (4개) - 회전 상태 0으로 그림
        const bulletCount = 4;
        g.lineStyle(2, 0xFFFFFF);
        for (let i = 0; i < bulletCount; i++) {
            const angle = (i / bulletCount) * Math.PI * 2;
            g.moveTo(cx, cy);
            g.lineTo(cx + Math.cos(angle) * radius * 1.2, cy + Math.sin(angle) * radius * 1.2);
        }

        // 중심점
        g.lineStyle(1, 0xFFFFFF);
        g.drawCircle(cx, cy, 5);
    }

    /**
     * Chaser 그리기
     */
    private drawChaser(g: Graphics, cx: number, cy: number, radius: number, healthRatio: number): void {
        // 이등변 삼각형 (앞쪽이 뾰족한 형태)
        const tipX = radius * 1.2;
        const backX = -radius * 0.8;
        const halfHeight = radius * 0.8;

        g.lineStyle(2, 0xFFFFFF);
        g.moveTo(cx + tipX, cy);
        g.lineTo(cx + backX, cy - halfHeight);
        g.lineTo(cx + backX, cy + halfHeight);
        g.closePath();

        // 내부 중심선 (방향 표시)
        g.lineStyle(1, 0xFFFFFF);
        g.moveTo(cx + backX * 0.3, cy);
        g.lineTo(cx + tipX * 0.5, cy);

        // 체력 표시 (내부 작은 삼각형)
        const scale = 0.4 * healthRatio;
        g.lineStyle(1, 0xFFFFFF, 0.5);
        g.moveTo(cx + tipX * scale, cy);
        g.lineTo(cx + backX * scale, cy - halfHeight * scale);
        g.lineTo(cx + backX * scale, cy + halfHeight * scale);
        g.closePath();
    }

    /**
     * Splitter 그리기
     */
    private drawSplitter(g: Graphics, cx: number, cy: number, radius: number, healthRatio: number, splitLevel: number): void {
        // 6각형
        const sides = 6;
        const points: number[] = [];

        for (let i = 0; i < sides; i++) {
            const angle = (i / sides) * Math.PI * 2 - Math.PI / 2;
            points.push(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius);
        }

        g.lineStyle(2, 0xFFFFFF);
        g.drawPolygon(points);

        // 체력 원
        g.lineStyle(1, 0xFFFFFF, 0.5);
        g.drawCircle(cx, cy, radius * 0.6 * healthRatio);

        // 분할 레벨 표시 (작은 원들)
        g.lineStyle(1, 0xFFFFFF);
        for (let i = 0; i <= splitLevel; i++) {
            g.drawCircle(cx + (i - splitLevel / 2) * 8, cy, 3);
        }
    }

    /**
     * Splitter 전용 텍스쳐 (splitLevel 포함)
     */
    getSplitterTexture(healthLevel: number, radius: number, splitLevel: number): Texture | null {
        if (!this.app) return null;

        const key = `splitter_${healthLevel}_${radius}_split${splitLevel}`;

        if (this.textures.has(key)) {
            return this.textures.get(key)!;
        }

        const size = radius * 2 * this.TEXTURE_PADDING;
        const healthRatio = healthLevel / 20;
        const offset = size / 2;

        const g = new Graphics();
        this.drawSplitter(g, offset, offset, radius, healthRatio, splitLevel);

        const renderTexture = RenderTexture.create({
            width: size,
            height: size,
            resolution: 2
        });

        this.app.renderer.render(g, { renderTexture });

        g.destroy();

        this.textures.set(key, renderTexture);
        return renderTexture;
    }

    /**
     * 스프라이트 생성
     */
    createBossSprite(type: BossType, healthLevel: number, radius: number, splitLevel?: number): Sprite | null {
        let texture: Texture | null;

        if (type === 'splitter' && splitLevel !== undefined) {
            texture = this.getSplitterTexture(healthLevel, radius, splitLevel);
        } else {
            texture = this.getOrCreateTexture(type, healthLevel, radius);
        }

        if (!texture) return null;

        const sprite = new Sprite(texture);
        sprite.anchor.set(0.5);
        return sprite;
    }

    /**
     * 초기화 여부
     */
    isInitialized(): boolean {
        return this.initialized;
    }

    /**
     * 정리
     */
    destroy(): void {
        for (const texture of this.textures.values()) {
            texture.destroy(true);
        }
        this.textures.clear();
        this.initialized = false;
        this.app = null;
        BossTextureCache.instance = null;
    }
}

// 글로벌 인스턴스
export const bossTextureCache = BossTextureCache.getInstance();
